import type { JSX } from 'react';

import { Text } from '@change/ds-core-ui/next';

import { translationKey } from '@change-corgi/core/i18n';
import { Translate, useI18n } from '@change-corgi/core/react/i18n';
import { Card, Separator } from '@change-corgi/design-system-legacy/components/content';
import { ComboBox } from '@change-corgi/design-system-legacy/components/forms';
import { Box } from '@change-corgi/design-system-legacy/layout';

import type { DecisionMaker as DecisionMakerType } from 'src/app/shared/hooks/decisionMakers';

import { DecisionMakerResult } from './components/DecisionMakerResult';
import { EmptyResult } from './components/EmptyResult';
import { SelectedDecisionMaker } from './components/SelectedDecisionMaker';
import { useDecisionMaker } from './hooks/useDecisionMaker';

export function DecisionMaker(): JSX.Element | null {
	const { data, actions } = useDecisionMaker();
	const { translate } = useI18n();

	const optionComponent = (decisionMaker: DecisionMakerType): JSX.Element => {
		if (!decisionMaker.isConfirmed) return <EmptyResult input={decisionMaker.displayName} />;
		return <DecisionMakerResult decisionMaker={decisionMaker} />;
	};

	return (
		<Box id="decision_makers" sx={{ mt: 12 }}>
			<Text variant="heading" size="x-small" className="lowercase first-letter:uppercase" as="p">
				<Translate value="corgi.petition.edit.petition_data.decision_maker.title" />
			</Text>
			<ComboBox
				id="decision-maker-combobox-field"
				name="decision-maker-combobox-field"
				placeholder={translate(translationKey('corgi.petition.edit.petition_data.decision_maker.placeholder'))}
				label=""
				inputValue={data.inputValue}
				onInputChange={actions.handleInputChange}
				value={data.value}
				onChange={actions.handleChange}
				options={data.options}
				optionToString={(decisionMaker) => decisionMaker.displayName}
				renderOption={optionComponent}
				filterOptions={(x) => x}
				data-qa="decision-maker-combobox-field"
				// overriding the combobox popover zIndex to place the listbox
				// over the content on the page -- a fix for correct placement of the listbox
				// should be moved to the design system
				sx={{
					// eslint-disable-next-line @typescript-eslint/naming-convention
					'& #decision-maker-combobox-field-popover': { zIndex: 5 },
					input: { height: '52px !important' },
					// eslint-disable-next-line @typescript-eslint/naming-convention
					'input + div': { marginTop: '11px !important' },
				}}
				className="mt-6"
			/>
			{data.selectedDecisionMakers.length > 0 && (
				<Card aria-live="polite" sx={{ mt: 24 }}>
					<Box
						as="ul"
						role="list"
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'stretch',
							justifyContent: 'space-between',
							textAlign: 'left',
							width: '100%',
						}}
					>
						{data.selectedDecisionMakers.map((decisionMaker, index) => (
							<Box as="li" role="listitem" key={decisionMaker.id}>
								<>
									{index !== 0 && <Separator sx={{ color: 'neutral-grey600' }} />}
									<SelectedDecisionMaker
										decisionMaker={decisionMaker}
										onRemoveDecisionMaker={() => actions.handleOnRemove(decisionMaker)}
										data-qa="decision-maker-combobox-field-option"
									/>
								</>
							</Box>
						))}
					</Box>
				</Card>
			)}
		</Box>
	);
}
